import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Plus, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormSection } from '@/components/forms/Form';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { Combobox } from '@/components/forms/Combobox';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { useUpsertEcheance } from '@/hooks/queries/useEcheances';
import { useUploadJustificatif } from '@/hooks/queries/useJustificatifs';
import { useUser } from '@/hooks/useUser';
import { formatDA } from '@/lib/format';
import { t } from '@/locales/i18n';
import type {
  ConditionsPaiement,
  EcheanceRow,
  EcheanceSens,
  EcheancePriorite,
  ModeReglement,
  TierRow,
  TypeTier,
  TypeTierFournisseur,
} from '@/lib/database.types';
import { TierQuickCreateDialog } from './TierQuickCreateDialog';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  sens: EcheanceSens;
  editing: EcheanceRow | null;
  onSaved: () => void;
}

const TIERS_TYPES_BY_SENS: Record<EcheanceSens, TypeTier[]> = {
  a_encaisser: ['client_b2b', 'client_b2c'],
  a_payer: ['fournisseur', 'sous_traitant', 'partenaire', 'formateur'],
};

const TYPE_TIER_FOURN_TO_TIER_TYPE: Record<TypeTierFournisseur, TypeTier> = {
  fournisseur: 'fournisseur',
  formateur: 'formateur',
  prestataire: 'sous_traitant',
  sous_traitant: 'sous_traitant',
  freelance: 'sous_traitant',
  bailleur: 'partenaire',
  autre: 'fournisseur',
};

const CONDITIONS_LABELS: Record<ConditionsPaiement, string> = {
  reception: 'À réception',
  '15j': '15 jours',
  '30j': '30 jours',
  '45j': '45 jours',
  '60j': '60 jours',
  '90j': '90 jours',
  perso: 'Personnalisé',
};

const CONDITIONS_DAYS: Record<Exclude<ConditionsPaiement, 'perso'>, number> = {
  reception: 0,
  '15j': 15,
  '30j': 30,
  '45j': 45,
  '60j': 60,
  '90j': 90,
};

const MODE_LABELS: Record<ModeReglement, string> = {
  especes: 'Espèces',
  virement: 'Virement',
  cheque: 'Chèque',
  traite: 'Traite',
  cb: 'Carte bancaire',
  mobile: 'Mobile (Edahabia/CIB)',
};

const TYPE_TIER_FOURN_LABELS: Record<TypeTierFournisseur, string> = {
  fournisseur: 'Fournisseur',
  formateur: 'Formateur',
  prestataire: 'Prestataire',
  sous_traitant: 'Sous-traitant',
  freelance: 'Freelance',
  bailleur: 'Bailleur',
  autre: 'Autre',
};

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function EcheanceFormDialog({
  open,
  onOpenChange,
  entrepriseId,
  sens,
  editing,
  onSaved,
}: Props): JSX.Element {
  const isEncaisser = sens === 'a_encaisser';
  const { user } = useUser();
  const { data: activites = [] } = useAllActivites();
  const { data: users = [] } = useUsersList();
  const upsert = useUpsertEcheance();
  const uploadJ = useUploadJustificatif();

  const today = new Date().toISOString().slice(0, 10);
  const echeance30 = addDays(today, 30);

  // ====== State ======
  const [activiteId, setActiviteId] = useState('');
  const [typeFournisseur, setTypeFournisseur] = useState<TypeTierFournisseur>('fournisseur');
  const [tierId, setTierId] = useState('');
  const [serviceId] = useState('');
  const [factureRef, setFactureRef] = useState('');
  const [devisRef, setDevisRef] = useState('');
  const [bonCommandeRef, setBonCommandeRef] = useState('');
  const [commercialId, setCommercialId] = useState('');

  const [montantHt, setMontantHt] = useState('0');
  const [tvaTaux, setTvaTaux] = useState('19');
  const [acompte, setAcompte] = useState('0');
  const [rasTaux, setRasTaux] = useState('0');
  const [devise, setDevise] = useState('DZD');
  const [taux, setTaux] = useState('1');

  const [dateEmission, setDateEmission] = useState(today);
  const [dateReceptionFacture, setDateReceptionFacture] = useState('');
  const [conditions, setConditions] = useState<ConditionsPaiement>('30j');
  const [dateEcheance, setDateEcheance] = useState(echeance30);
  const [modeReglement, setModeReglement] = useState<ModeReglement>('virement');

  const [priorite, setPriorite] = useState<EcheancePriorite>('normale');
  const [observation, setObservation] = useState('');
  const [motif, setMotif] = useState('');
  const [justificatifFile, setJustificatifFile] = useState<File | null>(null);
  const [justificatifUrl, setJustificatifUrl] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  // ====== Derived ======
  const activitesEnt = useMemo(
    () => activites.filter((a) => a.entreprise_id === entrepriseId),
    [activites, entrepriseId],
  );

  const tiersTypeFilter: TypeTier[] = isEncaisser
    ? TIERS_TYPES_BY_SENS.a_encaisser
    : [TYPE_TIER_FOURN_TO_TIER_TYPE[typeFournisseur]];

  const { data: filteredTiers = [], isLoading: tiersLoading } = useTiers({
    entrepriseId: entrepriseId || null,
    types: activiteId ? tiersTypeFilter : null,
    activiteId: activiteId || null,
  });

  const selectedTier = filteredTiers.find((tt) => tt.id === tierId);

  const ht = Number(montantHt) || 0;
  const tva = Number(tvaTaux) || 0;
  const ac = Number(acompte) || 0;
  const ras = Number(rasTaux) || 0;
  const tx = Number(taux) || 1;
  const ttc = Math.round(ht * (1 + tva / 100) * 100) / 100;
  const ttcDzd = Math.round(ttc * tx * 100) / 100;
  const restant = Math.max(ttc - ac, 0);
  const netAPayer = Math.round((ttc - (ttc * ras) / 100) * 100) / 100;

  const justificatifRequis = ttcDzd > 50000;
  const approbateurRequired = ttcDzd > 100000;
  const ibanRequiredButMissing =
    !isEncaisser && modeReglement === 'virement' && selectedTier && !selectedTier.iban;

  // ====== Effects ======
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setActiviteId(editing.activite_id ?? '');
      setTypeFournisseur((editing.type_tier_fournisseur ?? 'fournisseur') as TypeTierFournisseur);
      setTierId(editing.tier_id);
      setFactureRef(editing.facture_ref ?? '');
      setDevisRef(editing.devis_ref ?? '');
      setBonCommandeRef(editing.bon_commande_ref ?? '');
      setCommercialId(editing.commercial_id ?? '');
      setMontantHt(String(editing.montant_ht ?? editing.montant_initial));
      setTvaTaux(String(editing.tva_taux ?? 0));
      setAcompte(String(editing.acompte ?? 0));
      setRasTaux(String(editing.ras_taux ?? 0));
      setDevise(editing.devise_code);
      setTaux(String(editing.taux_change));
      setDateEmission(editing.date_emission);
      setDateReceptionFacture(editing.date_reception_facture ?? '');
      setConditions((editing.conditions_paiement ?? '30j') as ConditionsPaiement);
      setDateEcheance(editing.date_echeance);
      setModeReglement((editing.mode_reglement ?? 'virement') as ModeReglement);
      setPriorite(editing.priorite);
      setObservation(editing.observation ?? '');
      setMotif(editing.motif ?? '');
      setJustificatifUrl(editing.justificatif_url);
    } else {
      setActiviteId('');
      setTypeFournisseur('fournisseur');
      setTierId('');
      setFactureRef('');
      setDevisRef('');
      setBonCommandeRef('');
      setCommercialId(user?.id ?? '');
      setMontantHt('0');
      setTvaTaux(isEncaisser ? '0' : '19');
      setAcompte('0');
      setRasTaux('0');
      setDevise('DZD');
      setTaux('1');
      setDateEmission(today);
      setDateReceptionFacture('');
      setConditions('30j');
      setDateEcheance(echeance30);
      setModeReglement('virement');
      setPriorite('normale');
      setObservation('');
      setMotif('');
      setJustificatifFile(null);
      setJustificatifUrl(null);
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, isEncaisser]);

  // Reset tier quand on change activité ou type fournisseur
  useEffect(() => {
    if (open && !editing) setTierId('');
  }, [activiteId, typeFournisseur, open, editing]);

  // Auto-calcule date échéance depuis conditions + date émission
  useEffect(() => {
    if (conditions !== 'perso') {
      setDateEcheance(addDays(dateEmission, CONDITIONS_DAYS[conditions]));
    }
  }, [conditions, dateEmission]);

  const tiersOptions = useMemo(
    () =>
      filteredTiers.map((tt) => ({
        value: tt.id,
        label: `${tt.code ? tt.code + ' · ' : ''}${tt.raison_sociale}`,
        hint: tt.email ?? tt.telephone ?? undefined,
      })),
    [filteredTiers],
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!activiteId) return setError('Activité obligatoire');
    if (!tierId) return setError('Tiers obligatoire');
    if (ht <= 0) return setError('Montant HT > 0 requis');
    if (!factureRef.trim()) return setError('Référence facture obligatoire');
    if (dateEcheance < dateEmission) return setError('Date échéance < date émission');
    if (justificatifRequis && !justificatifFile && !justificatifUrl)
      return setError('Justificatif obligatoire au-delà de 50 000 DA');
    if (ibanRequiredButMissing)
      return setError(
        'IBAN/RIB du tiers manquant pour un virement. Modifier la fiche tiers ou changer le mode de règlement.',
      );

    let uploadedUrl: string | null = justificatifUrl;
    if (justificatifFile) {
      try {
        const j = await uploadJ.mutateAsync({
          entreprise_id: entrepriseId,
          file: justificatifFile,
          related_type: 'echeance',
        });
        uploadedUrl = j.file_path;
      } catch (err) {
        return setError('Upload justificatif échoué : ' + (err as Error).message);
      }
    }

    try {
      await upsert.mutateAsync({
        id: editing?.id,
        entreprise_id: entrepriseId,
        sens,
        tier_id: tierId,
        activite_id: activiteId || null,
        service_id: serviceId || null,
        type_tier_fournisseur: isEncaisser ? null : typeFournisseur,
        facture_ref: factureRef.trim(),
        devis_ref: devisRef.trim() || null,
        bon_commande_ref: bonCommandeRef.trim() || null,
        date_emission: dateEmission,
        date_echeance: dateEcheance,
        date_reception_facture: dateReceptionFacture || null,
        montant_initial: ttc,
        montant_initial_dzd: ttcDzd,
        montant_ht: ht,
        tva_taux: tva,
        montant_ttc: ttc,
        acompte: ac,
        ras_taux: ras,
        net_a_payer: netAPayer,
        montant_paye: ac > 0 ? ac : 0,
        devise_code: devise,
        taux_change: tx,
        conditions_paiement: conditions,
        mode_reglement: modeReglement,
        priorite,
        justificatif_url: uploadedUrl,
        commercial_id: commercialId || null,
        approbateur_required: approbateurRequired,
        motif: motif || null,
        observation: observation || null,
      });
      toast.success(editing ? 'Échéance mise à jour' : 'Échéance créée');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  const tierEmptyAction = (
    <Button type="button" variant="outline" size="sm" onClick={() => setQuickCreateOpen(true)}>
      <Plus className="h-3.5 w-3.5" /> Créer un nouveau tiers
    </Button>
  );

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title={
          editing
            ? `Échéance ${editing.reference}`
            : isEncaisser
              ? t('echeances.form.addEncaisser')
              : t('echeances.form.addPayer')
        }
        description={
          isEncaisser
            ? 'Encours client : émettez une créance détaillée (HT/TVA/TTC, conditions, justificatif > 50K).'
            : "Dette fournisseur : saisissez la facture reçue, le mode de règlement et l'approbateur si > 100K."
        }
        onSubmit={handleSubmit}
        isSubmitting={upsert.isPending || uploadJ.isPending}
        size="xl"
      >
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <FormSection title="Identification">
          <FormSelect
            label="Activité"
            value={activiteId}
            onChange={setActiviteId}
            required
            options={[
              { value: '', label: '— Sélectionner une activité —' },
              ...activitesEnt.map((a) => ({ value: a.id, label: a.nom })),
            ]}
          />
          {!isEncaisser && (
            <FormSelect
              label="Type de tiers"
              value={typeFournisseur}
              onChange={(v) => setTypeFournisseur(v as TypeTierFournisseur)}
              required
              options={(Object.keys(TYPE_TIER_FOURN_LABELS) as TypeTierFournisseur[]).map((t2) => ({
                value: t2,
                label: TYPE_TIER_FOURN_LABELS[t2],
              }))}
            />
          )}
          <FormField
            label={isEncaisser ? 'Référence facture' : 'Référence facture fournisseur'}
            value={factureRef}
            onChange={(e) => setFactureRef(e.target.value)}
            required
            placeholder={isEncaisser ? 'FAC-2026-0142' : 'leur n° de facture'}
          />
          {isEncaisser ? (
            <FormField
              label="N° devis lié (optionnel)"
              value={devisRef}
              onChange={(e) => setDevisRef(e.target.value)}
              placeholder="DEV-2026-XXX"
            />
          ) : (
            <FormField
              label="Bon de commande lié (optionnel)"
              value={bonCommandeRef}
              onChange={(e) => setBonCommandeRef(e.target.value)}
              placeholder="BC-2026-XXX"
            />
          )}
        </FormSection>

        <FormSection title="Tiers">
          <Combobox
            label={isEncaisser ? 'Client' : 'Tiers (fournisseur/prestataire)'}
            value={tierId}
            onChange={setTierId}
            options={tiersOptions}
            disabled={!activiteId || tiersLoading}
            loading={tiersLoading}
            required
            resultCount={tiersOptions.length}
            emptyMessage={
              !activiteId
                ? "Choisissez d'abord une activité"
                : 'Aucun tiers correspondant à cette activité / type'
            }
            emptyAction={activiteId ? tierEmptyAction : null}
            placeholder="Rechercher par nom, code, email…"
            fullWidth
          />
          {isEncaisser && (
            <FormSelect
              label="Commercial responsable"
              value={commercialId}
              onChange={setCommercialId}
              options={[
                { value: '', label: '— Aucun —' },
                ...users.map((u) => ({
                  value: u.user_id,
                  label: u.full_name ?? u.user_id.slice(0, 8),
                })),
              ]}
              hint="Auto-rempli avec l'utilisateur courant si rôle commercial."
            />
          )}
          {!isEncaisser && selectedTier && (
            <div className="rounded-md border border-border bg-muted/30 p-2 text-xs sm:col-span-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-muted-foreground">IBAN : </span>
                  {selectedTier.iban ? (
                    <span className="font-mono">{selectedTier.iban}</span>
                  ) : (
                    <span className="text-destructive">non renseigné</span>
                  )}
                  {selectedTier.banque_nom && (
                    <span className="ml-1 text-muted-foreground">({selectedTier.banque_nom})</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Contact : </span>
                  {selectedTier.contact_nom ?? '—'}
                </div>
              </div>
              {!selectedTier.iban && (
                <a
                  href={`/parametres/tiers?id=${selectedTier.id}`}
                  className="mt-1 inline-flex items-center gap-1 text-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="h-3 w-3" /> Modifier la fiche tiers
                </a>
              )}
            </div>
          )}
        </FormSection>

        <FormSection title="Montants">
          <FormField
            label="Montant HT"
            type="number"
            step="0.01"
            min="0"
            value={montantHt}
            onChange={(e) => setMontantHt(e.target.value)}
            required
          />
          <FormSelect
            label="TVA %"
            value={tvaTaux}
            onChange={setTvaTaux}
            options={[
              { value: '0', label: '0 % (exonéré)' },
              { value: '9', label: '9 %' },
              { value: '19', label: '19 % (standard)' },
            ]}
          />
          <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
            <div className="text-muted-foreground">Montant TTC (calculé)</div>
            <div className="num text-sm font-semibold">{formatDA(ttc)}</div>
          </div>
          <FormSelect
            label="Devise"
            value={devise}
            onChange={(v) => {
              setDevise(v);
              if (v === 'DZD') setTaux('1');
            }}
            options={[
              { value: 'DZD', label: 'DZD' },
              { value: 'EUR', label: 'EUR' },
              { value: 'USD', label: 'USD' },
            ]}
          />
          {devise !== 'DZD' && (
            <FormField
              label="Taux de change → DZD"
              type="number"
              step="0.000001"
              min="0.000001"
              value={taux}
              onChange={(e) => setTaux(e.target.value)}
            />
          )}
          {isEncaisser ? (
            <>
              <FormField
                label="Acompte déjà versé (optionnel)"
                type="number"
                step="0.01"
                min="0"
                value={acompte}
                onChange={(e) => setAcompte(e.target.value)}
              />
              <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
                <div className="text-muted-foreground">Solde restant dû</div>
                <div className="num text-sm font-semibold">{formatDA(restant)}</div>
              </div>
            </>
          ) : (
            <>
              <FormSelect
                label="Retenue à la source (RAS) %"
                value={rasTaux}
                onChange={setRasTaux}
                options={[
                  { value: '0', label: '0 % (aucune)' },
                  { value: '5', label: '5 %' },
                  { value: '10', label: '10 % (formateur/freelance)' },
                  { value: '15', label: '15 %' },
                ]}
              />
              <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
                <div className="text-muted-foreground">Net à payer</div>
                <div className="num text-sm font-semibold">{formatDA(netAPayer)}</div>
              </div>
            </>
          )}
        </FormSection>

        <FormSection title="Dates & Conditions">
          <FormField
            label={isEncaisser ? 'Date facture' : 'Date facture fournisseur'}
            type="date"
            value={dateEmission}
            onChange={(e) => setDateEmission(e.target.value)}
            required
          />
          {!isEncaisser && (
            <FormField
              label="Date réception facture"
              type="date"
              value={dateReceptionFacture}
              onChange={(e) => setDateReceptionFacture(e.target.value)}
              hint="Peut différer de la date facture fournisseur."
            />
          )}
          <FormSelect
            label="Conditions de paiement"
            value={conditions}
            onChange={(v) => setConditions(v as ConditionsPaiement)}
            required
            options={(
              ['reception', '15j', '30j', '45j', '60j', '90j', 'perso'] as ConditionsPaiement[]
            ).map((c) => ({ value: c, label: CONDITIONS_LABELS[c] }))}
          />
          <FormField
            label="Date échéance"
            type="date"
            value={dateEcheance}
            onChange={(e) => {
              setDateEcheance(e.target.value);
              if (conditions !== 'perso') setConditions('perso');
            }}
            required
            hint={conditions === 'perso' ? 'Date saisie manuellement' : 'Auto-calculée'}
          />
          <FormSelect
            label="Mode de règlement"
            value={modeReglement}
            onChange={(v) => setModeReglement(v as ModeReglement)}
            required
            options={(
              ['especes', 'virement', 'cheque', 'traite', 'cb', 'mobile'] as ModeReglement[]
            ).map((m) => ({ value: m, label: MODE_LABELS[m] }))}
          />
        </FormSection>

        <FormSection title="Suivi">
          <FormSelect
            label="Priorité"
            value={priorite}
            onChange={(v) => setPriorite(v as EcheancePriorite)}
            options={[
              { value: 'normale', label: 'Normale' },
              { value: 'haute', label: 'Haute' },
              { value: 'vip', label: 'VIP' },
            ]}
          />
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Justificatif{justificatifRequis && <span className="text-destructive"> *</span>}
            </label>
            {justificatifRequis && !justificatifFile && !justificatifUrl && (
              <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 p-2 text-xs text-warning">
                <AlertTriangle className="h-3 w-3" />
                Obligatoire au-delà de 50 000 DA
              </div>
            )}
            <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 text-sm hover:bg-secondary">
              {justificatifFile?.name ??
                (justificatifUrl ? 'Justificatif déjà uploadé' : 'Choisir un fichier')}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  setJustificatifFile(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <FormField
            label="Motif"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            fullWidth
            placeholder="Description courte de la créance / dette"
          />
          <FormTextarea
            label="Observation interne"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            fullWidth
            rows={2}
            placeholder="Visible uniquement en interne — non envoyé au tiers."
          />
        </FormSection>

        {!isEncaisser && approbateurRequired && (
          <FormSection title="Validation">
            <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs sm:col-span-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-warning" />
                <div>
                  <div className="font-semibold text-warning">Approbation requise</div>
                  <div>
                    Montant TTC en DZD = {formatDA(ttcDzd)} (
                    {ttcDzd > 500000
                      ? '> 500 000 DA — direction + super_admin'
                      : '> 100 000 DA — direction'}
                    ). Une fois l&apos;échéance créée, soumettez-la pour validation.
                  </div>
                </div>
              </div>
            </div>
          </FormSection>
        )}
      </FormDialog>

      <TierQuickCreateDialog
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        entrepriseId={entrepriseId}
        defaultActiviteId={activiteId}
        defaultType={
          isEncaisser
            ? 'client_b2b'
            : (TYPE_TIER_FOURN_TO_TIER_TYPE[typeFournisseur] satisfies TypeTier)
        }
        onCreated={(t2: TierRow) => {
          setTierId(t2.id);
          setQuickCreateOpen(false);
        }}
      />
    </>
  );
}
